import { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import type { CheckboxChangeEvent } from "primereact/checkbox";
import { ProgressSpinner } from "primereact/progressspinner";
import { Sidebar } from "primereact/sidebar";
import { TabView, TabPanel } from "primereact/tabview";
import { Tooltip } from "primereact/tooltip";
import type { TreeSelectionEvent, TreeExpandedEvent } from "primereact/tree";
import type { TreeNode } from "primereact/treenode";
import { PathTree, ValidationMessage } from "sj-ui-toolkit/src/components";
import { PathCreationTableView } from "./PathCreationTableView";
import { themeClasses, LESSON_TYPE_DISPLAY_LABELS } from "sj-ui-toolkit/src/atlas/utils";
import { calculatePathDuration, addTooltipClassesToTreeData } from "@/api/recommendedAssignments";
import type { LessonNode } from "@/api/recommendedAssignments";
import type { Lesson } from "@/api/lessons";

export interface PathCreationValues {
  selectedPaths?: Record<string, { checked: boolean; partialChecked: boolean }>;
  retakePreviousLessons?: boolean;
}

interface PathCreationProps {
  values?: PathCreationValues;
  handleChange: (values: PathCreationValues) => void;
  showValidationMessages?: boolean;
  validationErrors?: { hasError?: boolean; message?: string };
  treeData?: LessonNode[];
  lessonOrder?: string[];
  isLoading?: boolean;
  lessonMap?: Record<string, Lesson>;
}

type SidebarItem = {
  label?: string;
  description?: string;
  lessonType?: string;
  duration?: number | string;
};

export const PathCreation = ({
  values = {},
  handleChange,
  showValidationMessages = false,
  validationErrors = {},
  treeData = [],
  lessonOrder = [],
  isLoading,
  lessonMap = {},
}: PathCreationProps) => {
  const [selectedKeys, setSelectedKeys] = useState<
    Record<string, { checked: boolean; partialChecked: boolean }>
  >(values.selectedPaths ?? {});
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});
  const [retakePreviousLessons, setRetakePreviousLessons] = useState(
    values.retakePreviousLessons ?? false
  );
  const [sidebarItem, setSidebarItem] = useState<SidebarItem | null>(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [pathSummaryItems, setPathSummaryItems] = useState<Lesson[]>([]);
  const [treeStylingComplete, setTreeStylingComplete] = useState(false);

  const proficiencyClassMapper = (node: LessonNode, level: string) => {
    const customClasses: string[] = [];
    if (level === "proficiency") {
      if (node.label?.includes("Growth Opportunities"))
        customClasses.push(themeClasses.textWarning);
      else if (node.label?.includes("Proficiencies")) customClasses.push(themeClasses.textPrimary);
      else if (node.label?.includes("Strengths")) customClasses.push(themeClasses.textSuccess);
    }
    return customClasses;
  };

  const processedTreeData = useMemo(() => {
    if (!treeData.length) return [];
    return addTooltipClassesToTreeData(treeData, proficiencyClassMapper);
    // proficiencyClassMapper references theme tokens only; stable between renders
  }, [treeData]);

  useEffect(() => {
    const allKeys: Record<string, boolean> = {};
    const collect = (nodes: LessonNode[]) => {
      nodes.forEach(node => {
        if (node.key != null) allKeys[String(node.key)] = true;
        if (node.children) collect(node.children);
      });
    };
    if (processedTreeData.length > 0) {
      collect(processedTreeData);
      setExpandedKeys(allKeys);
    }
    if (!values.selectedPaths || Object.keys(values.selectedPaths).length === 0) {
      if (processedTreeData.length > 0) handleUseRecommended();
    }
    // handleUseRecommended is stable — defined below; processedTreeData is the dep
  }, [lessonMap, processedTreeData]);

  useEffect(() => {
    if (processedTreeData.length > 0) setTreeStylingComplete(true);
    return () => setTreeStylingComplete(false);
  }, [processedTreeData]);

  const pathDuration = useMemo(
    () => calculatePathDuration(lessonMap, lessonOrder),
    [lessonMap, lessonOrder]
  );

  useEffect(() => {
    const summaryLessons = lessonOrder
      .map(key => lessonMap[key])
      .filter((l): l is Lesson => l !== undefined);
    setPathSummaryItems(summaryLessons);
  }, [lessonOrder, lessonMap]);

  const handleSelectionChange = (e: TreeSelectionEvent) => {
    const val = e.value as Record<string, { checked: boolean; partialChecked: boolean }>;
    setSelectedKeys(val);
    handleChange({ ...values, selectedPaths: val });
  };

  const handleTableSelectionChange = (e: { value: unknown }) => {
    const val = e.value as Record<string, { checked: boolean; partialChecked: boolean }>;
    setSelectedKeys(val);
    handleChange({ ...values, selectedPaths: val });
  };

  const handleClearSelected = () => {
    setSelectedKeys({});
    handleChange({ ...values, selectedPaths: {} });
  };

  const handleUseRecommended = () => {
    const buildSelectedKeys = (nodes: LessonNode[]) => {
      const result: Record<string, { checked: boolean; partialChecked: boolean }> = {};

      const traverse = (node: LessonNode, ancestors: LessonNode[] = []) => {
        if (!node.children || node.children.length === 0) return;
        const isTopicLevel = ancestors.length === 2;
        if (isTopicLevel) {
          const lessons = node.children.filter(
            child => !child.children || child.children.length === 0
          );
          const firstLesson = lessons[0];
          if (firstLesson?.key) {
            result[String(firstLesson.key)] = { checked: true, partialChecked: false };
            if (node.key) {
              result[String(node.key)] =
                lessons.length === 1
                  ? { checked: true, partialChecked: false }
                  : { checked: false, partialChecked: true };
            }
            ancestors.forEach(ancestor => {
              if (ancestor.key)
                result[String(ancestor.key)] = { checked: false, partialChecked: true };
            });
          }
        }
        node.children.forEach(child => traverse(child, [...ancestors, node]));
      };

      const [firstRating, ...otherRatings] = nodes;
      if (firstRating) traverse(firstRating, []);

      const markAllUnselected = (node: LessonNode) => {
        if (node.key) result[String(node.key)] = { checked: false, partialChecked: false };
        node.children?.forEach(markAllUnselected);
      };
      otherRatings.forEach(markAllUnselected);
      return result;
    };

    const newSelectedKeys = buildSelectedKeys(processedTreeData);
    setSelectedKeys(newSelectedKeys);
    handleChange({ ...values, selectedPaths: newSelectedKeys });
  };

  const handleRetakePreviousLessonsChange = (e: CheckboxChangeEvent) => {
    const val = e.checked ?? false;
    setRetakePreviousLessons(val);
    handleChange({ ...values, retakePreviousLessons: val });
  };

  const handleShowMoreClick = (item: TreeNode) => {
    setSidebarItem(item as SidebarItem);
    setSidebarVisible(true);
  };

  return (
    <div className="p-4" data-testid="path-creation-container">
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-6 justify-between items-start">
            {/* LEFT COLUMN */}
            <div className="flex-1 min-w-0 min-w-[300px]">
              <p className={`mb-4 ${themeClasses.textGray700}`}>
                Select the lessons you would like to include in this Assignment.
              </p>

              <div className="mb-4 flex gap-3">
                <Button
                  label="Use Recommended"
                  className="p-button"
                  onClick={handleUseRecommended}
                  data-testid="path-creation-use-recommended-button"
                />
                <Button
                  label="Clear Selections"
                  className="ml-auto"
                  severity="secondary"
                  onClick={handleClearSelected}
                  data-testid="path-creation-clear-selected-button"
                />
              </div>

              <div className="mb-4 flex items-center gap-2">
                <Checkbox
                  inputId="retakePreviousLessons"
                  checked={retakePreviousLessons}
                  onChange={handleRetakePreviousLessonsChange}
                  data-testid="path-creation-retake-checkbox"
                />
                <label htmlFor="retakePreviousLessons" className="ml-1 cursor-pointer">
                  Have users retake previously completed lessons
                </label>
              </div>
            </div>

            {/* RIGHT COLUMN – SUMMARY */}
            <div className="flex-shrink-0 min-w-[180px] text-right">
              <div className="body-lg font-semibold mb-4">Assignment Summary</div>
              <div className="space-y-2">
                <div>
                  <div className={`body-xs ${themeClasses.textGray500} uppercase tracking-wide`}>
                    Duration
                  </div>
                  <div className={`body-lg font-medium ${themeClasses.textPrimary}`}>
                    {pathDuration || "0 minutes"}
                  </div>
                </div>
                <div>
                  <div className={`body-xs ${themeClasses.textGray500} uppercase tracking-wide`}>
                    Lessons
                  </div>
                  <div className={`body-lg font-medium ${themeClasses.textPrimary}`}>
                    {pathSummaryItems.length}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="relative min-h-[600px]">
            <TabView aria-label="View mode">
              <TabPanel header="Table View">
                {isLoading ? (
                  <div className="text-center py-8">
                    <ProgressSpinner style={{ width: "40px", height: "40px" }} strokeWidth="5" />
                  </div>
                ) : (
                  <PathCreationTableView
                    treeData={processedTreeData}
                    selectedKeys={selectedKeys}
                    onSelectionChange={handleTableSelectionChange}
                    onShowMoreClick={item => handleShowMoreClick(item as TreeNode)}
                    className="w-full"
                  />
                )}
              </TabPanel>

              <TabPanel header="List View">
                <PathTree
                  value={processedTreeData as Parameters<typeof PathTree>[0]["value"]}
                  emptyMessage={
                    isLoading ? (
                      <ProgressSpinner style={{ width: "40px", height: "40px" }} strokeWidth="5" />
                    ) : (
                      "No lessons found"
                    )
                  }
                  selectionKeys={selectedKeys}
                  onSelectionChange={handleSelectionChange}
                  expandedKeys={expandedKeys}
                  onToggle={(e: TreeExpandedEvent) =>
                    setExpandedKeys(e.value as Record<string, boolean>)
                  }
                  className="w-full !bg-transparent"
                  filter
                  sidebarShowMore
                  onShowMoreClick={handleShowMoreClick}
                  data-testid="path-creation-tree"
                />

                {treeStylingComplete && (
                  <>
                    <Tooltip
                      target="span.p-tree-node-content.tree-level-1"
                      content="Proficiency Score"
                      position="top"
                    />
                    <Tooltip
                      target="span.p-tree-node-content.tree-level-2"
                      content="Assessment Category"
                      position="top"
                    />
                    <Tooltip
                      target="span.p-tree-node-content.tree-level-3"
                      content="Assessment Topic"
                      position="top"
                    />
                    <Tooltip
                      target="span.p-tree-node-content.tree-level-4"
                      content="Recommended Lesson"
                      position="top"
                    />
                  </>
                )}
              </TabPanel>
            </TabView>
          </div>

          {showValidationMessages && validationErrors?.hasError && (
            <div className="mt-2">
              <ValidationMessage severity="error" text={validationErrors.message ?? ""} />
            </div>
          )}
        </div>
      </div>

      <Sidebar
        visible={sidebarVisible}
        position="right"
        onHide={() => setSidebarVisible(false)}
        modal
        blockScroll
        pt={{ mask: { style: { backdropFilter: "blur(5px)" } } }}
      >
        <h2 className="text-xl font-semibold mb-4 pb-4 border-b">Lesson Details</h2>

        {sidebarItem && (
          <>
            <div className="mb-4 pb-4 border-b">
              <h3 className="font-semibold">Title:</h3>
              <h4>{sidebarItem.label}</h4>
            </div>
            <div className="mb-4 pb-4 border-b">
              <h3 className="font-semibold">Description:</h3>
              <p>{sidebarItem.description}</p>
            </div>
            <div className="mb-4 pb-4 border-b">
              <h3 className="font-semibold">Type:</h3>
              <p>{LESSON_TYPE_DISPLAY_LABELS[sidebarItem.lessonType ?? ""] ?? ""}</p>
            </div>
            <div className="mb-4">
              <h3 className="font-semibold">Duration:</h3>
              <p>{sidebarItem.duration} minutes</p>
            </div>
          </>
        )}
      </Sidebar>
    </div>
  );
};
